import type { Feature, FeatureCollection, Polygon } from "geojson";
import { isPolygonValid, polygonContains } from "../../../domain/geometry/operations/validate";
import { validateZonePlacement } from "../../../domain/zone/zoneValidation";
import { Zone } from "../../../domain/zone/Zone";
import type { ZoneType } from "../../../domain/zone/types";
import type { AnyPolygonFeature, GeometryResult } from "../../../domain/geometry/types";
import type { UndoableCommand } from "../undoStack";

export interface DrawZoneInput {
	drawn: Feature<Polygon>;
	zoneId: string;
	zoneName: string;
	zoneType: ZoneType;
	boundary: AnyPolygonFeature;
}

export interface DrawZoneResult {
	command: UndoableCommand;
	zone: Zone;
	zonesFC: FeatureCollection;
}

/**
 * Validate a drawn polygon as a new zone (must be inside boundary).
 * If fully inside an existing zone, add as hole; otherwise append as new feature.
 */
export function drawZoneUseCase(
	input: DrawZoneInput,
	existingFC: FeatureCollection,
	applyResult: (fc: FeatureCollection) => void,
	revert: (fc: FeatureCollection) => void,
): GeometryResult<DrawZoneResult> {
	const validation = isPolygonValid(input.drawn);
	if (!validation.isValid) {
		return { ok: false, error: validation.message ?? "Invalid polygon" };
	}

	const zone = new Zone(input.zoneId, input.zoneName, input.zoneType, input.drawn);

	const placement = validateZonePlacement(zone, input.boundary);
	if (!placement.ok) return placement;

	// Check if drawn is fully inside an existing zone → add as hole
	const containingIdx = existingFC.features.findIndex((f) => polygonContains(f as AnyPolygonFeature, input.drawn));

	let newFC: FeatureCollection;
	let label: string;

	if (containingIdx >= 0) {
		const target = existingFC.features[containingIdx] as Feature<Polygon>;
		const withHole: Feature<Polygon> = {
			...target,
			geometry: {
				...target.geometry,
				coordinates: [...target.geometry.coordinates, input.drawn.geometry.coordinates[0]],
			},
		};
		const holeValidation = isPolygonValid(withHole);
		if (!holeValidation.isValid) {
			return { ok: false, error: holeValidation.message ?? "Invalid hole" };
		}
		newFC = {
			...existingFC,
			features: existingFC.features.map((f, i) => (i === containingIdx ? withHole : f)),
		};
		label = `Add ${input.zoneName} hole`;
	} else {
		newFC = { ...existingFC, features: [...existingFC.features, zone.geometry] };
		label = `Draw ${input.zoneName}`;
	}

	return {
		ok: true,
		data: {
			zone,
			zonesFC: newFC,
			command: {
				label,
				execute: () => applyResult(newFC),
				undo: () => revert(existingFC),
			},
		},
	};
}
